import React from 'react';
import { identity } from 'lodash';

import {
  always,
  ATTRIBUTE_LIMIT,
  EMPTY_PREFILL_ATTRIBUTES,
} from '@neptune/shared/attributes-domain';
import { convertRegexpSearchToFilter, useMemoizedValue } from '@neptune/shared/common-util';
import { ShortIdsByProject } from '@neptune/shared/entity-domain';
import {
  unwindSubpropertyStrategies,
  UnwindSubpropertyStrategy,
} from '@neptune/shared/entity-leaderboard-business-logic';
import { queryAttributeDefinitions } from '@neptune/shared/leaderboard-domain';
import { regexpSearchFilter } from '@neptune/shared/search-business-logic';
import { QueryFilterSearch } from '@neptune/shared/search-domain';

import {
  AttributeDefinition,
  AttributeType,
  convertAttributeToSearchString,
} from 'domain/experiment/attribute';

import { uniqAttributeDefinitions } from './uniq-attribute-definitions';

export type QueryAttributeDefinitionDataSourceBase<T> = {
  subpropertyStrategy?: UnwindSubpropertyStrategy;
  enhancer?: (input: AttributeDefinition) => T;
  prefillAttributes?: AttributeDefinition[];
  filterAttributeTypes?: AttributeType[];
  filter?: (input: T) => boolean;
  limit?: number;
};

export type QueryAttributeDefinitionDataSourceMultiProject<T> =
  QueryAttributeDefinitionDataSourceBase<T> & {
    shortIdsByProject: ShortIdsByProject;
    projectIdentifiers?: never;
    projectIdentifier?: never;
    shortIds?: never;
  };

export type QueryAttributeDefinitionDataSourceSingleProject<T> =
  QueryAttributeDefinitionDataSourceBase<T> & {
    shortIds?: string[];
    projectIdentifier: string;
    projectIdentifiers?: never;
    shortIdsByProject?: never;
  };

export type QueryAttributeDefinitionDataSourceMultiProjectsNoShortIds<T> =
  QueryAttributeDefinitionDataSourceBase<T> & {
    shortIds?: never;
    projectIdentifiers: string[];
    projectIdentifier?: never;
    shortIdsByProject?: never;
  };

export type QueryAttributeDefinitionDataSourceParams<T> =
  | QueryAttributeDefinitionDataSourceSingleProject<T>
  | QueryAttributeDefinitionDataSourceMultiProject<T>
  | QueryAttributeDefinitionDataSourceMultiProjectsNoShortIds<T>;

export function createQueryAttributeDefinitionDataSource<
  T extends AttributeDefinition = AttributeDefinition,
>({
  filterAttributeTypes,
  enhancer = identity,
  filter = always,
  limit = ATTRIBUTE_LIMIT,
  prefillAttributes = EMPTY_PREFILL_ATTRIBUTES,
  projectIdentifiers,
  projectIdentifier,
  shortIds,
  shortIdsByProject,
  subpropertyStrategy = UnwindSubpropertyStrategy.none,
}: QueryAttributeDefinitionDataSourceParams<T>): QueryFilterSearch<T> {
  const rawQuery = async (search: string) => {
    const attributeNameFilter = convertRegexpSearchToFilter(search);

    if (projectIdentifier !== undefined) {
      return queryAttributeDefinitions({
        limit,
        shortIds,
        projectIdentifier,
        filterAttributeTypes,
        attributeNameFilter,
      });
    }

    if (shortIdsByProject !== undefined) {
      return queryAttributeDefinitions({
        limit,
        shortIdsByProject,
        filterAttributeTypes,
        attributeNameFilter,
      });
    }

    if (projectIdentifiers !== undefined) {
      return queryAttributeDefinitions({
        limit,
        projectIdentifiers,
        filterAttributeTypes,
        attributeNameFilter,
      });
    }

    throw new Error(
      'useAttributeDefinitionDataSource: missing project scope (project(s)/shortIds)',
    );
  };

  const prepareOutput = (collection: AttributeDefinition[], search: string) => {
    const collectionWithSubproperties =
      unwindSubpropertyStrategies[subpropertyStrategy](collection);

    const collectionSource = uniqAttributeDefinitions(
      prefillAttributes,
      collectionWithSubproperties,
    );

    const enhancedCollection = collectionSource.map(enhancer).filter(filter);

    const allResults = regexpSearchFilter(
      search,
      enhancedCollection,
      convertAttributeToSearchString,
    );

    return allResults;
  };

  return async (query: string) => {
    const trimmedQuery = query.trim();
    const { attributeDefinitions, nextPageToken } = await rawQuery(trimmedQuery);
    const entries = prepareOutput(attributeDefinitions, trimmedQuery);

    return {
      entries,
      thereIsMore: !!nextPageToken,
    };
  };
}

export function useQueryAttributeDefinitionDataSource<T>(
  params: QueryAttributeDefinitionDataSourceMultiProject<T>,
): QueryFilterSearch<T>;
export function useQueryAttributeDefinitionDataSource<T>(
  params: QueryAttributeDefinitionDataSourceSingleProject<T>,
): QueryFilterSearch<T>;
export function useQueryAttributeDefinitionDataSource<T>(
  params: QueryAttributeDefinitionDataSourceMultiProjectsNoShortIds<T>,
): QueryFilterSearch<T>;

export function useQueryAttributeDefinitionDataSource<
  T extends AttributeDefinition = AttributeDefinition,
>(params: QueryAttributeDefinitionDataSourceParams<T>): QueryFilterSearch<T> {
  const memoizedParams = useMemoizedValue(params);

  return React.useCallback(
    (query: string) => {
      return createQueryAttributeDefinitionDataSource(memoizedParams)(query);
    },
    [memoizedParams],
  );
}
